<!-- Enhanced Freeze Sidebar -->
<p-drawer
  [visible]="visible()"
  [showCloseIcon]="false"
  position="right"
  [blockScroll]="true"
  (onHide)="HideSidebar()"
  [dismissible]="false"
  [closeOnEscape]="!isPaymentFlowBlockingUi"
  appendTo="body"
  styleClass="w-full payment-sidebar p-0 no-header"
  aria-label="enhanced-freeze-sidebar"
  data-testid="enhanced-freeze-sidebar"
>
  <ng-template #headless>
    <!-- Header -->
    <div class="flex align-items-center border-bottom-1 border-300 surface-50">
      <div
        class="w-3rem h-3rem cursor-pointer surface-200 hover:surface-300 flex align-items-center justify-content-center transition-colors transition-duration-200"
        (click)="HideSidebar()"
        aria-label="close-sidebar-button"
        data-testid="close-sidebar-button"
      >
        <i class="fa fa-times text-lg text-700 hover:text-900"></i>
      </div>
      <div class="flex-1 px-3 py-2">
        <h4 class="text-lg font-semibold text-900 m-0" data-testid="sidebar-header">
          {{ getHeaderText() | translate }}
        </h4>
      </div>
    </div>

    <div class="flex flex-column h-full surface-0">
      <!-- Client Info Section (already extracted) -->
      <freeze-client-info
        [client]="client()"
        [clientId]="getClientId()"
        [tenantId]="getTenantId()"
      />

      <!-- Main Content Area -->
      <div class="flex-1 overflow-y-auto p-3">

        <!-- Skeleton Loader -->
        @if (isInitialLoading$ | async) {
          <div class="surface-0 shadow-3 border-round-lg p-4 mb-3">
            <div class="flex align-items-center gap-3 pb-3 border-bottom-1 border-200">
              <p-skeleton width="6rem" height="1.5rem" borderRadius="16px" />
              <p-skeleton width="12rem" height="1.5rem" styleClass="flex-1" />
            </div>
            <div class="pt-3">
              <div class="surface-100 border-round p-3">
                <p-skeleton width="8rem" height="0.875rem" styleClass="mb-2" />
                <p-skeleton width="10rem" height="1.25rem" styleClass="mb-2" />
                <p-skeleton width="100%" height="1rem" styleClass="mb-1" />
                <p-skeleton width="100%" height="1rem" />
              </div>
            </div>
          </div>
          <div class="surface-0 shadow-3 border-round-lg p-4 mb-3">
            <p-skeleton width="8rem" height="1.25rem" styleClass="mb-3" />
            <div class="grid">
              <div class="col-12 md:col-6">
                <p-skeleton width="6rem" height="0.875rem" styleClass="mb-2" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>
              <div class="col-12 md:col-6">
                <p-skeleton width="6rem" height="0.875rem" styleClass="mb-2" />
                <p-skeleton width="100%" height="2.5rem" />
              </div>
            </div>
            <p-skeleton width="100%" height="3rem" styleClass="mt-3" />
          </div>
          <div class="surface-0 shadow-3 border-round-lg p-4 mb-3">
            <p-skeleton width="8rem" height="1.25rem" styleClass="mb-3" />
            <p-skeleton width="100%" height="2.5rem" styleClass="mb-2" />
            <p-skeleton width="100%" height="3rem" />
          </div>
          <div class="surface-0 shadow-3 border-round-lg p-4 mb-3">
            <p-skeleton width="8rem" height="1.25rem" styleClass="mb-3" />
            <p-skeleton width="100%" height="5rem" styleClass="mb-2" />
            <p-skeleton width="100%" height="5rem" />
          </div>
          <div class="flex justify-content-end gap-2 mt-4">
            <p-skeleton width="6rem" height="2.5rem" />
            <p-skeleton width="8rem" height="2.5rem" />
          </div>
        }

        <!-- Actual Content -->
        @if (!(isInitialLoading$ | async)) {

          <!-- Subscription Info Card -->
          <div
            class="surface-0 shadow-3 border-round-lg p-4 mb-3"
            data-testid="subscription-info-card"
          >
            <div class="flex align-items-center gap-3 pb-3 border-bottom-1 border-200">
              <p-tag
                [value]="'POS.PLANTYPE.' + subscription().subscription_category.toString().toUpperCase() | translate"
                severity="info"
                styleClass="text-xs font-semibold"
                [attr.data-testid]="'subscription-category-' + subscription().id"
              />
              <h3
                class="text-lg font-bold text-900 m-0 flex-1"
                [attr.data-testid]="'subscription-name-' + subscription().id"
              >
                {{ subscription().name_primary_language }}
              </h3>
            </div>
            <!-- Freeze Attempts Info -->

            @if(!isStopMode && !isCancelMode && !isRescheduleMode) {
            <div class="pt-3" data-testid="freeze-attempts-list">
              <div class="surface-100 border-round p-3">
                <label
                  class="block text-xs text-700 mb-2 font-semibold uppercase"
                  data-testid="freeze-attempts-label"
                >
                  {{ 'FREEZE.FREEZE_SIDEBAR.LABEL_FREEZEATTEMPTS' | translate }}
                </label>
                <div
                  class="text-base font-bold text-900 mb-2"
                  [attr.data-testid]="'freeze-attempts-count-' + subscription().id"
                >
                  {{ subscription().freeze_allowed_number_of_attempts_in_year }}
                  {{ 'FREEZE.FREEZE_SIDEBAR.STRONG_EACHYEAR' | translate }}
                </div>
                @for (rf of CycleRemainingInYear; track rf) {
                  <div
                    class="flex justify-content-between align-items-center text-sm py-1"
                    [attr.data-testid]="'freeze-remaining-' + subscription().id + '-' + rf.year"
                  >
                    <span class="text-600">{{ rf.year }}</span>
                    <span class="font-semibold">
                      <span class="text-primary">{{
                        subscription().freeze_allowed_number_of_attempts_in_year < rf.count
                          ? 0
                          : subscription().freeze_allowed_number_of_attempts_in_year - rf.count
                      }}</span>
                      <span class="text-600">/{{ subscription().freeze_allowed_number_of_attempts_in_year }} Remaining</span>
                    </span>
                  </div>
                }
              </div>
            </div>
          }
          </div>

          <!-- Requested dates (approve mode) -->
          @if (isApproveMode && hasApproveRequestedDates()) {
            <section
              class="mb-3 p-2 md:p-3 bg-blue-50 border-1 border-primary border-round"
              data-testid="approve-requested-dates-section"
              aria-label="approve-requested-dates-section"
              role="region"
            >
              <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div class="flex align-items-center gap-2 text-xs font-semibold uppercase text-primary">
                  <span
                    class="inline-flex align-items-center justify-content-center w-2rem h-2rem border-circle bg-primary text-white flex-shrink-0"
                    aria-hidden="true">
                    <i class="fa fa-calendar-days text-xs"></i>
                  </span>
                  {{ 'FREEZE.DETAILS.FREEZE_PERIOD' | translate }}
                </div>
                @if (approveFreezeDays; as days) {
                  <span
                    class="inline-flex align-items-center px-2 py-1 bg-primary text-white border-round text-xs font-bold white-space-nowrap"
                    data-testid="approve-freeze-days-message"
                    aria-label="approve-freeze-days-message">
                    {{ 'FREEZE.DETAILS.DURATION_DAYS' | translate : { days } }}
                  </span>
                }
              </div>

              <div class="flex flex-column md:flex-row align-items-stretch gap-2">
                <article
                  class="flex-1 surface-0 border-1 border-200 border-round p-2 md:p-3 shadow-1 min-w-0"
                  data-testid="approve-requested-start-date"
                  aria-label="approve-requested-start-date">
                  <span class="block text-xs text-600 font-semibold uppercase mb-1">
                    {{ 'FREEZE.DETAILS.FROM' | translate }}
                  </span>
                  <span class="block text-base md:text-lg font-bold text-900 line-height-2">
                    @if (approveRequestedStartDateRaw) {
                      {{ approveRequestedStartDateRaw | datetimeformat:'defaultformat' }}
                    } @else if (approveRequestedStartDate) {
                      {{ approveRequestedStartDate | datetimeformat:'defaultformat' }}
                    }
                  </span>
                </article>

                <div
                  class="flex align-items-center justify-content-center flex-shrink-0 text-primary"
                  aria-hidden="true">
                  <i class="fa fa-arrow-down text-lg block md:hidden"></i>
                  <i class="fa fa-long-arrow-right text-xl hidden md:block"></i>
                </div>

                <article
                  class="flex-1 surface-0 border-1 border-200 border-round p-2 md:p-3 shadow-1 min-w-0"
                  data-testid="approve-requested-end-date"
                  aria-label="approve-requested-end-date">
                  <span class="block text-xs text-600 font-semibold uppercase mb-1">
                    {{ 'FREEZE.DETAILS.TO' | translate }}
                  </span>
                  <span class="block text-base md:text-lg font-bold text-900 line-height-2">
                    @if (approveRequestedEndDateRaw) {
                      {{ approveRequestedEndDateRaw | datetimeformat:'defaultformat' }}
                    } @else if (approveRequestedEndDate) {
                      {{ approveRequestedEndDate | datetimeformat:'defaultformat' }}
                    }
                  </span>
                </article>
              </div>
            </section>
          }

          <!-- Form Section -->
          <form
            [formGroup]="fg"
            (submit)="createFreeze()"
            class="surface-0 shadow-3 border-round-lg p-4 mb-3"
            data-testid="enhanced-freeze-form"
          >
            <!-- Freeze Details (stop/cancel modes only) -->
            @if ((isStopMode || isCancelMode) && ishavefreezeToRescheduleObject()) {
              <div
                class="mb-4 p-3 surface-50 border-round-lg border-1 border-200"
                data-testid="freeze-details-section"
              >
                <h5 class="text-base font-semibold text-900 mb-3">
                  <i class="fa fa-snowflake-o mr-2 text-primary"></i>
                  {{ 'FREEZE.FREEZE_SIDEBAR.FREEZE_DETAILS' | translate }}
                </h5>
                <div class="grid">
                  <div class="col-12 md:col-6">
                    <label class="block text-xs text-600 mb-1 font-semibold uppercase">
                      {{ 'FREEZE.FREEZE_SIDEBAR.LABEL_STARTDATE' | translate }}
                    </label>
                    <div class="text-base text-900 font-semibold" data-testid="freeze-start-date-display">
                      {{ freezeStartDate | date:'dd MMM yyyy' }}
                    </div>
                  </div>
                  <div class="col-12 md:col-6">
                    <label class="block text-xs text-600 mb-1 font-semibold uppercase">
                      {{ 'FREEZE.FREEZE_SIDEBAR.LABEL_ENDDATE' | translate }}
                    </label>
                    <div class="text-base text-900 font-semibold" data-testid="freeze-end-date-display">
                      {{ freezeEndDate | date:'dd MMM yyyy' }}
                    </div>
                  </div>
                </div>
              </div>
            }

            <!-- Stop Date picker (stop mode only) -->
            @if (isStopMode && ishavefreezeToRescheduleObject()) {
              <div class="mb-4" data-testid="stop-date-section">
                <label
                  class="block text-sm text-700 mb-2 font-semibold"
                  for="stopdate"
                  data-testid="stop-date-label"
                >
                  {{ 'FREEZE.FREEZE_SIDEBAR.LABEL_STOP_DATE' | translate }} *
                </label>
                <p-datepicker
                  inputId="stopdate"
                  formControlName="stopdate"
                  [minDate]="stopDateMin"
                  [maxDate]="stopDateMax"
                  dateFormat="dd M yy"
                  [showIcon]="true"
                  [autofocus]="false"
                  appendTo="body"
                  styleClass="w-full"
                  aria-label="stop-date-picker"
                  data-testid="stop-date-picker"
                />
                @if (StopDateControl?.touched && StopDateControl?.invalid) {
                  <app-error-msg
                    [errors]="StopDateControl?.errors"
                    [fieldLabel]="'FREEZE.FREEZE_SIDEBAR.LABEL_STOP_DATE' | translate"
                    data-testid="stop-date-error"
                  />
                }
              </div>
            }

            <!-- Date Selection -->
            @if (!isStopMode && !isCancelMode && !isApproveMode) {
              <freeze-date-selection
                [formGroup]="fg"
                [freezeV2Flow]="freezeV2Flow"
                [isRecurring]="IsRecurringSubscription"
                [freezeCanOnlyBillingCycle]="subscription().freeze_can_only_billing_cycle"
                [filteredTimeline]="filteredFreezeTimeline$.value"
                [selectionLimit]="selectionLimit"
              />
            }

            <!-- Freeze Cycle Adjustment Details — only for recurring subscriptions -->
            @if (freezeV2Flow && IsRecurringSubscription && ((freezeCycleAdjustmentLoading$ | async) || (freezeCycleAdjustment$ | async))) {
              <div class="mt-3" data-testid="freeze-cycle-adjustment-inline">
                <freeze-cycle-adjustment-details
                  [cycleInfo]="freezeCycleAdjustment$ | async"
                  [isLoading]="(freezeCycleAdjustmentLoading$ | async) ?? false"
                  [showTitle]="true"
                />
              </div>
            }

            <!-- Freeze Reason -->
            @if (freezeV2Flow && !isRescheduleMode && !isStopMode && !isCancelMode) {
              <div class="mb-4 mt-2" data-testid="freeze-reason-section">
                <label
                  class="block text-sm text-700 mb-2 font-semibold"
                  data-testid="freeze-reason-label"
                >
                  {{ 'FREEZE.FREEZE_SIDEBAR.FREEZE_REASON' | translate }} *
                </label>
                <p-select
                  [options]="(freezeReasons$ | async) || []"
                  formControlName="freezeReason"
                  optionLabel="reason_pri_lang"
                  placeholder="{{ 'FREEZE.FREEZE_SIDEBAR.SELECT_REASON' | translate }}"
                  class="w-full"
                  appendTo="body"
                  [loading]="freezeReasonsLoading$ | async"
                  aria-label="freeze-reason-dropdown"
                  data-testid="freeze-reason-dropdown"
                >
                  <ng-template let-reason pTemplate="item">
                    <div
                      class="flex flex-column py-2"
                      [pTooltip]="reason.reason_pri_lang + (reason.required_doc_pri_lang ? '\n' + 'Required Docs: ' + reason.required_doc_pri_lang : '')"
                      tooltipPosition="left"
                    >
                      <span
                        class="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                        style="max-width: 280px"
                      >{{ reason.reason_pri_lang }}</span>
                      @if (reason.required_doc_pri_lang) {
                        <small
                          class="text-600 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                          style="max-width: 280px"
                        >
                          {{ 'FREEZE.FREEZE_SIDEBAR.REQUIRED_DOCS' | translate }}: {{ reason.required_doc_pri_lang }}
                        </small>
                      }
                    </div>
                  </ng-template>
                </p-select>
                @if (FreezeReasonControl?.touched && FreezeReasonControl?.invalid) {
                  <app-error-msg
                    [errors]="FreezeReasonControl?.errors"
                    [fieldLabel]="'FREEZE.FREEZE_SIDEBAR.FREEZE_REASON' | translate"
                    data-testid="freeze-reason-error"
                  />
                }
                <!-- Required Documents Info -->
                @if (getSelectedFreezeReason()?.required_doc_pri_lang) {
                  <div class="mt-3" data-testid="required-docs-info">
                    <p-message severity="info" styleClass="w-full">
                      <ng-template pTemplate="content">
                        <div class="flex align-items-start gap-2">
                          <i class="pi pi-info-circle text-blue-500 mt-1"></i>
                          <div>
                            <div class="font-semibold text-900 mb-1">
                              {{ 'FREEZE.FREEZE_SIDEBAR.REQUIRED_DOCUMENTS' | translate }}:
                            </div>
                            <div class="text-sm text-700">
                              {{ getSelectedFreezeReason()?.required_doc_pri_lang }}
                            </div>
                          </div>
                        </div>
                      </ng-template>
                    </p-message>
                  </div>
                }
              </div>
            }

            <!-- Freeze Charge Breakdown -->
            @if (freezeV2Flow && signupCharges.length > 0) {
              <freeze-charge-breakdown
                [charges]="signupCharges"
                [isLoading]="freezeChargeLoading$.value"
                [applyDiscount]="applyDiscount"
                [discountApplied]="discountApplied"
                [inValidDiscount]="inValidDiscount"
                [singleStaffDiscount]="single_staff_discount"
                [isInclusive]="isInclusive"
                [chargeAmountsArray]="ChargeAmountsFormArray"
                [chargeDiscountsArray]="ChargeDiscountsFormArray"
                (applyDiscountToggle)="onApplyDiscountToggle()"
                (cancelDiscount)="CancelFreezeDiscount()"
                (removeDiscount)="RemoveFreezeDiscount()"
                (chargeDiscountBlur)="onChargeDiscountBlur($event)"
                (chargeAmountBlur)="onChargeAmountBlur($event)"
              />
            }

            <!-- Comments -->
            <freeze-comments
              [formGroup]="fg"
              [freezeV2Flow]="freezeV2Flow"
              [isRescheduleMode]="isRescheduleMode"
              [isStopMode]="isStopMode"
              [isCancelMode]="isCancelMode"
            />

            <!-- File Upload -->
            @if (freezeV2Flow && !isRescheduleMode && !isStopMode && !isCancelMode && false) { <!-- Remove false after api issue resolved -->
              <freeze-file-upload
                [uploadedFiles]="uploadedFiles"
                [isUploading]="isUploadingFile$.value"
                [selectedFreezeReason]="getSelectedFreezeReason()"
                (fileSelected)="handleFileSelect($event)"
                (fileRemoved)="removeFile($event)"
              />
            }

            <!-- Action Buttons -->
            <div class="text-center" data-testid="action-buttons-section">
              <!-- Payment Buttons -->
              @if (canShowPaymentButtons()) {
                <div class="mb-3">
                  <p-button
                    [loading]="isSubmitting$ | async"
                    (onClick)="handleOnlinePayment()"
                    [disabled]="isOnlineBtnDisabled()"
                    styleClass="w-full h-3rem font-semibold"
                    severity="success"
                    [attr.aria-label]="'continue-online-payment-button'"
                    [attr.data-testid]="'continue-online-payment-button'"
                  >
                    <i class="fa fa-credit-card mr-2"></i>
                    {{ 'POS.MEMBERSHIP_ADDON.CONTINUE_ONLINE_PAYMENT' | translate }}
                  </p-button>
                </div>
                <div>
                  <p-button
                    (onClick)="handleOfflinePayment()"
                    [loading]="isSubmitting$ | async"
                    [disabled]="isOfflineBtnDisabled()"
                    styleClass="w-full h-3rem"
                    severity="secondary"
                    [attr.aria-label]="'record-offline-payment-button'"
                    [attr.data-testid]="'record-offline-payment-button'"
                  >
                    <i class="fa fa-money-bills mr-2"></i>
                    {{ 'common.BUTTONS.RECORD_OFFLINE_PAYMENT_BTN' | translate }}
                  </p-button>
                </div>
                @if (showPayViaSavedPaymentMethods) {
                  <div class="mt-3">
                    <p-button
                      (onClick)="OpenSavedPaymentMethods()"
                      [loading]="isSubmitting$ | async"
                      [disabled]="isOnlineBtnDisabled()"
                      styleClass="w-full h-3rem"
                      severity="secondary"
                      [attr.aria-label]="'pay-with-saved-card-button'"
                      [attr.data-testid]="'pay-with-saved-card-button'"
                    >
                      <i class="fa fa-credit-card mr-2"></i>
                      {{ 'POS.MEMBERSHIP_ADDON.PAY_WITH_SAVED_METHOD' | translate }}
                    </p-button>
                  </div>
                }
                @if (showChargeOnPosTerminal) {
                  <div class="mt-3">
                    <p-button
                      (onClick)="PosDeviceFreeze()"
                      [loading]="isSubmitting$ | async"
                      [disabled]="isOfflineBtnDisabled()"
                      styleClass="w-full h-3rem"
                      severity="secondary"
                      [attr.aria-label]="'charge-on-pos-button'"
                      [attr.data-testid]="'charge-on-pos-button'"
                    >
                      <img width="14" src="assets/img/icons/icon-terminal.svg" alt="POS Terminal" class="mr-2" style="pointer-events: none;" />
                      {{ 'POS_DEVICE.CHARGE_ON_POS' | translate }}
                    </p-button>
                  </div>
                }
              }

              <!-- Submit Button -->
              @if (canShowSubmitButton()) {
                <p-button
                  type="submit"
                  [label]="getSubmitButtonLabel()"
                  styleClass="w-full h-3rem font-semibold"
                  severity="primary"
                  [loading]="(btnLoading | async) || (isSubmitting$ | async) || false"
                  [disabled]="!fg.valid || (isSubmitting$ | async) || !isAdjustmentCalculated() || (freezeCycleAdjustmentLoading$ | async)"
                  aria-label="submit-freeze-button"
                  data-testid="submit-freeze-button"
                />
              }
            </div>
          </form>
        }
      </div>
    </div>
  </ng-template>
</p-drawer>

@if (showErrorPopup | async; as show_Popup) {
  <app-errors
    [visible]="show_Popup"
    [errorData]="errorMessages"
    [error]="error"
    (hideErrorPopup)="HideErrorPopup($event)"
    aria-label="error-dialog"
    data-testid="error-dialog"
  />
}

<!-- Other Payment Popup -->
@if (showOtherPaymentsModel$ | async) {
  <other-payment
    [otherPaymentsModal]="showOtherPaymentsModel$ | async"
    [isCashRecurring]="true"
    [totalAmtTobePaid]="paymentAmount"
    [showCreditBalance]="true"
    [creditBalance]="creditBalance"
    [minimumPaymentRequiredPerc]="0"
    [tenantId]="tenantId()"
    [IsMinimumPaymentRequired]="false"
    [showCreditBalanceForClientCredit]="false"
    (hideOtherPaymentDialog)="HideOtherPaymentDialog($event)"
    (closeOtherPaymentDialog)="closeOtherPaymentDialog($event)"
  />
}

@if (showDeviceList) {
  <pos-device-list
    [visible]="showDeviceList"
    [loadReady]="posGatewaysLoaded"
    [tenantId]="getTenantId()"
    [activeOnly]="true"
    [autoSelectSingle]="true"
    (closeDevicesPopup)="closeDevicesPopup($event)" />
}

@if (showSavedPaymentMethodsPopup.value) {
  <saved-payment-methods-popup
    [visible]="showSavedPaymentMethodsPopup.value"
    [clientId]="getClientId()"
    [tenantId]="getTenantId()"
    [currencyCode]="currencyCode"
    (paymentMethodSelected)="onSavedPaymentMethodSelected($event)"
    (hideDialog)="onSavedPaymentMethodsPopupClosed()" />
}

@if (paymentFailed) {
  <p-dialog [visible]="paymentFailed" [resizable]="false" appendTo="body" [modal]="true"
    [breakpoints]="{'767px': '100vw'}" [style]="{width: '550px'}" styleClass="no-header-modal overflow-visible">
    <ng-template pTemplate="header" styleClass="hidden" />
    <div class="p-3 align-items-center justify-content-between border-bottom-1 border-300 text-right">
      <i class="fa fa-times text-xl text-600 cursor-pointer" (click)="closePaymentFailed()" [attr.aria-label]="'close-payment-failed-dialog'"></i>
    </div>
    <ng-template pTemplate="content" styleClass="p-0">
      <div class="p-5 text-center">
        <i class="fa fa-times-circle p-error text-8xl"></i>
        <h5 class="text-lg p-error mb-3 mt-5">{{ 'POS_DEVICE.PAYMENT_FAILED' | translate }}</h5>
        @if (paymentFailedMessages.length > 0) {
          @for (message of paymentFailedMessages; track message) {
            <p class="mb-5">{{ message }}</p>
          }
        } @else {
          <p class="mb-5">{{ 'POS_DEVICE.FAILED_REASON' | translate }}</p>
        }
        <p-button [label]="'POS_DEVICE.RETRY_PAYMENT' | translate" (click)="RetryPayment()" styleClass="text-sm w-12rem" [attr.aria-label]="'retry-payment-button'" [attr.data-testid]="'retry-payment-button'" />
      </div>
    </ng-template>
  </p-dialog>
}

@if (transactionInProcess) {
  <p-dialog [visible]="transactionInProcess" [resizable]="false" appendTo="body" [modal]="true"
    [closeOnEscape]="false" [dismissableMask]="false" [blockScroll]="true" [closable]="false" [baseZIndex]="12000"
    [breakpoints]="{'767px': '100vw'}" [style]="{width: '550px'}" styleClass="no-header-modal overflow-visible">
    <ng-template pTemplate="header" styleClass="hidden" />
    <ng-template pTemplate="content" styleClass="p-0">
      <div class="p-5 text-center">
        <p-progressSpinner styleClass="w-8rem h-8rem" strokeWidth="4" animationDuration=".5s" />
        <h5 class="text-lg p-error mb-3 mt-5">{{ 'POS_DEVICE.TRANSACTION_PROGRESS' | translate }}</h5>
        <p class="mb-5">{{ 'POS_DEVICE.DO_NOT_CLOSE' | translate }}</p>
      </div>
    </ng-template>
  </p-dialog>
}

@if (paymentConfirmationPending) {
  <p-dialog [visible]="paymentConfirmationPending" [resizable]="false" appendTo="body" [modal]="true"
    [closeOnEscape]="paymentConfirmationTimedOut" [dismissableMask]="false" [blockScroll]="true" [baseZIndex]="12000"
    [breakpoints]="{'767px': '100vw'}" [style]="{width: '550px'}" styleClass="no-header-modal overflow-visible"
    [closable]="false">
    @if (paymentConfirmationTimedOut) {
      <div class="p-3 align-items-center justify-content-end border-bottom-1 border-300 text-right">
        <button type="button" class="border-none bg-transparent p-0 cursor-pointer"
          (click)="closePaymentConfirmationTimeout()"
          [attr.aria-label]="'close-payment-confirmation-timeout-dialog'"
          [attr.data-testid]="'close-payment-confirmation-timeout-dialog'">
          <i class="fa fa-times text-xl text-600"></i>
        </button>
      </div>
    } @else {
      <ng-template pTemplate="header" styleClass="hidden" />
    }
    <ng-template pTemplate="content" styleClass="p-0">
      <div class="p-5 text-center" data-testid="payment-confirmation-dialog">
        @if (paymentConfirmationTimedOut) {
          <i class="fa fa-clock-o text-orange-500 text-8xl"></i>
          <h5 class="text-lg text-orange-500 mb-3 mt-5">{{ paymentConfirmationTimeoutMessage }}</h5>
        } @else {
          <p-progressSpinner styleClass="w-8rem h-8rem" strokeWidth="4" animationDuration=".5s" />
          <h5 class="text-lg mb-3 mt-5">{{ 'POS_DEVICE.CONFIRMING_PAYMENT_STATUS' | translate }}</h5>
          @if (paymentConfirmationShowTimeLimit) {
            <p class="mb-2 font-semibold text-primary" data-testid="payment-confirmation-countdown">
              {{ 'POS_DEVICE.PAYMENT_TIME_REMAINING' | translate: { time: paymentConfirmationCountdownDisplay } }}
            </p>
            <p class="mb-3 text-sm text-600">{{ 'POS_DEVICE.PAYMENT_TIME_LIMIT_INFO' | translate }}</p>
          }
          <p class="mb-5">{{ 'POS_DEVICE.DO_NOT_CLOSE' | translate }}</p>
        }
      </div>
    </ng-template>
  </p-dialog>
}
